import type { ScreenSchema } from './types';

/**
 * Schema for the `question` and `confirm` templates.
 *
 * Slot → audio-marker key convention:
 *   'intro'            → screen.id
 *   'feedback'         → ${screen.id}-feedback         (single-string feedback)
 *   'feedback-correct' → ${screen.id}-feedback-correct
 *   'feedback-incorrect' → ${screen.id}-feedback-incorrect
 *   'feedback-retry'   → ${screen.id}-feedback-retry
 *   'feedback-retry-final' → ${screen.id}-feedback-retry-final
 *
 * Option-specific feedback slots ('feedback-1', 'feedback-2', …) are resolved
 * by audioKeyForSlot automatically at runtime — no static schema entries needed
 * because the S3 key is derived directly from the slot name.
 * JiT narration for option-specific feedback is not supported in this version.
 *
 * The `options` slot is transcript-only — option text is not narrated at runtime
 * but will be included in future VO script / word-doc transcript exports.
 */
const questionSchema: ScreenSchema = {
  template: ['question', 'confirm'],
  slots: {
    intro: {
      parts: [
        { field: 'heading', fallback: ['subheading', 'title'] },
        { field: 'text' },
        { field: 'instructions' },
        { field: 'question' }
      ]
    },

    // Single flat feedback string (screen.feedback is a plain string)
    feedback: {
      parts: [{ field: 'feedback' }]
    },

    // Conditional feedback variants (screen.feedback is an object)
    'feedback-correct': {
      parts: [{ field: 'feedback.correct' }]
    },
    'feedback-incorrect': {
      parts: [{ field: 'feedback.incorrect' }]
    },
    'feedback-retry': {
      parts: [{ field: 'feedback.retry' }]
    },
    'feedback-retry-final': {
      parts: [{ field: 'feedback.retry-final' }]
    },

    // Options are transcript-only — visible on screen but not narrated during play
    options: {
      use: 'transcript',
      iterate: 'options',
      keyPrefix: 'option',
      parts: [
        // Options can be a string value stored at key '0' (correct) or '1' (incorrect)
        { field: '0', omitIfPresent: ['1'] },
        { field: '1', omitIfPresent: ['0'] }
      ]
    }
  }
};

export default questionSchema;
